import { createReducer } from '@reduxjs/toolkit';

import {
  queueUploadsAction,
  rejectUploadsAction,
  removeRejectedUploadAction,
  removeUploadsAction,
  resetNonCompletedUploadsAction,
  resetUploadsAction,
  setAllFilesUploaded,
  setLargeUploadCancellablesAction,
  setLargeUploadProgressAction,
  setLargeUploadUrlInfoAction,
  setSmallUploadCancellablesAction,
  setSmallUploadProgressAction,
  setUploadAbortedAction,
  setUploadCompletedAction,
  setUploadedPartInfo,
  setUploadFailedAction,
  setUploadImageLoadingAction,
  setUploadImagePreviewAction,
  setUploadingSpeedAction,
  setUploadPreparingAction,
  setUploadSkippedAction,
  setUploadsPausedAction,
  setUploadsQueuedAction,
  setUploadsResumedAction,
  setUploadUploadingAction,
  setUploadUrlInfoAction,
} from './actions';
import { type Upload, type UploaderState, UploadStatus } from './types';

const getRejectedMessage = (message: string) => {
  switch (message) {
    case 'empty': {
      return 'File is empty';
    }
    case 'unsupported': {
      return 'File type not permitted';
    }
    case 'noExtension': {
      return 'File has no extension';
    }
    default: {
      return 'Unkown reason';
    }
  }
};

const initialState: UploaderState = {
  currentUploadingInfo: {
    speed: null,
    startedAt: 0,
    uploadedBytes: 0,
  },
  rejectedUploads: [],
  uploadProgress: {},
  uploadsArray: [],
};

export const reducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setUploadPreparingAction, (state, action) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === action.payload.uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].status = UploadStatus.preparing;
      }
    })
    .addCase(setUploadUploadingAction, (state, action) => {
      if (!state.currentUploadingInfo.startedAt) {
        state.currentUploadingInfo.startedAt = Date.now();
      }

      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === action.payload.uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].status = UploadStatus.uploading;
      }
    })
    .addCase(setUploadsQueuedAction, (state, action) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === action.payload.uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].status = UploadStatus.queued;
      }
    })
    .addCase(setUploadAbortedAction, (state, action) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === action.payload.uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].status = UploadStatus.aborted;
        state.uploadsArray[uploadIndex].xhr = undefined;
      }
    })
    .addCase(setUploadFailedAction, (state, { payload: { uploadId, errorMessage } }) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].status = UploadStatus.failed;
        state.uploadsArray[uploadIndex].errorMessage = errorMessage;
        state.uploadsArray[uploadIndex].xhr = undefined;

        delete state.uploadProgress[uploadId];
        state.uploadsArray[uploadIndex].s3Info = { totalBytesUploaded: 0, uploadedParts: [], uploadUrlInfo: undefined };
      }
    })
    .addCase(setUploadCompletedAction, (state, action) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === action.payload.uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].status = UploadStatus.completed;
        state.uploadsArray[uploadIndex].completedAt = Date.now();
        state.uploadsArray[uploadIndex].xhr = undefined;
      }
    })
    .addCase(setSmallUploadCancellablesAction, (state, action) => {
      const { uploadId, xhr, uploadUrlInfo } = action.payload;
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].clipId = uploadUrlInfo ? uploadUrlInfo.id : '';
        // @ts-expect-error it does not like WriteableDraft xhr being assigned this
        state.uploadsArray[uploadIndex].xhr = xhr;
        if (state.uploadsArray[uploadIndex].s3Info) {
          state.uploadsArray[uploadIndex].s3Info.uploadUrlInfo = uploadUrlInfo;
        } else {
          state.uploadsArray[uploadIndex].s3Info = {
            uploadUrlInfo: uploadUrlInfo,
          };
        }
      }
    })
    .addCase(setLargeUploadCancellablesAction, (state, action) => {
      const { uploadId, xhr, uploadUrlInfo } = action.payload;
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].clipId = uploadUrlInfo ? uploadUrlInfo.id : '';
        // @ts-expect-error it does not like WriteableDraft xhr being assigned this
        state.uploadsArray[uploadIndex].xhr = state.uploadsArray[uploadIndex].xhr
          ? { ...state.uploadsArray[uploadIndex].xhr, ...xhr }
          : { ...xhr };
        if (state.uploadsArray[uploadIndex].s3Info) {
          state.uploadsArray[uploadIndex].s3Info.uploadUrlInfo = uploadUrlInfo;
        } else {
          state.uploadsArray[uploadIndex].s3Info = {
            uploadUrlInfo: uploadUrlInfo,
          };
        }
      }
    })
    .addCase(setUploadImagePreviewAction, (state, action) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === action.payload.uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].imageBase64 = action.payload.imageBase64;
        state.uploadsArray[uploadIndex].isImageLoading = false;
      }
    })
    .addCase(setUploadSkippedAction, (state, action) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === action.payload.uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].status = UploadStatus.queued;
        state.uploadsArray[uploadIndex].xhr = undefined;
      }
    })
    .addCase(removeUploadsAction, (state, { payload: { uploadIds } }) => {
      state.uploadsArray = state.uploadsArray.filter((u) => !uploadIds.includes(u.id));
    })
    .addCase(rejectUploadsAction, (state, action) => {
      action.payload.files.forEach((file) => {
        state.rejectedUploads.push({
          date: Date.now(), // added for TS.... may affect handling of removeRejectedUploadAction
          id: file.name, // added for TS
          message: getRejectedMessage(file.reason),
          name: file.name,
          reason: file.reason,
          status: UploadStatus.rejected,
        });
      });
    })
    .addCase(removeRejectedUploadAction, (state, action) => {
      state.rejectedUploads = state.rejectedUploads.filter(
        ({ name, date }) =>
          !(action.payload.rejectedUpload.name === name && action.payload.rejectedUpload.date === date),
      );
    })
    .addCase(resetUploadsAction, (state) => {
      state.uploadsArray = [];
      state.rejectedUploads = [];
      state.uploadProgress = {};
      state.currentUploadingInfo = { ...initialState.currentUploadingInfo };
    })
    .addCase(resetNonCompletedUploadsAction, (state) => {
      state.uploadsArray = state.uploadsArray.filter((u) => u.status === UploadStatus.completed);
      state.uploadsArray.forEach((u) => {
        delete state.uploadProgress[u.id];
      });
      state.rejectedUploads = [];
      state.currentUploadingInfo = { ...initialState.currentUploadingInfo };
    })
    .addCase(queueUploadsAction, (state, action) => {
      // @ts-expect-error it does not like the writable wrapper
      const uploadsArray: Upload[] = action.payload.moveToTop
        ? [...action.payload.uploads, ...state.uploadsArray]
        : [...state.uploadsArray, ...action.payload.uploads];

      return {
        ...state,
        uploadsArray,
      };
    })

    .addCase(setSmallUploadProgressAction, (state, { payload: { uploadId, progress, abort } }) => {
      if (!state.uploadProgress[uploadId]) {
        state.uploadProgress[uploadId] = { progress, progressBeforePause: 0 };
        state.currentUploadingInfo.uploadedBytes += state.uploadProgress[uploadId].progress;
      } else if (progress >= state.uploadProgress[uploadId].progress) {
        const uploadDiff = progress - state.uploadProgress[uploadId].progress;
        state.uploadProgress[uploadId].progress = progress;

        state.currentUploadingInfo.uploadedBytes += uploadDiff;
      }
      state.uploadProgress[uploadId].abort = abort;
    })

    .addCase(setLargeUploadProgressAction, (state, { payload: { progress, partNumber, uploadId, abort } }) => {
      if (!state.uploadProgress[uploadId]) {
        state.uploadProgress[uploadId] = { progress: progress, progressBeforePause: 0 };
        state.currentUploadingInfo.uploadedBytes += state.uploadProgress[uploadId].progress;
      } else {
        const currentPartProgress = state.uploadProgress[uploadId].partsProgress?.[partNumber];

        if (!currentPartProgress || currentPartProgress < progress) {
          if (state.uploadProgress[uploadId].partsProgress) {
            state.uploadProgress[uploadId].partsProgress[partNumber] = progress;
          } else {
            state.uploadProgress[uploadId].partsProgress = { [partNumber]: progress };
          }
          const totalProgress = Object.keys(state.uploadProgress[uploadId].partsProgress).reduce(
            (sum, partProgress) => {
              return sum + (state.uploadProgress[uploadId].partsProgress?.[parseInt(partProgress, 10)] ?? 0);
            },
            0,
          );
          const uploadDiff = totalProgress - state.uploadProgress[uploadId].progress;
          state.uploadProgress[uploadId].progress = totalProgress;

          state.currentUploadingInfo.uploadedBytes += uploadDiff;
        }
      }
      state.uploadProgress[uploadId].abort = abort;
    })
    .addCase(setUploadImageLoadingAction, (state, { payload: { uploadId, isImageLoading } }) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].isImageLoading = isImageLoading;
      }
    })
    .addCase(setAllFilesUploaded, (state) => {
      state.currentUploadingInfo = { ...initialState.currentUploadingInfo };
    })
    .addCase(setUploadingSpeedAction, (state, { payload: { speed } }) => {
      state.currentUploadingInfo.speed = speed;
    })
    .addCase(setUploadsPausedAction, (state, { payload: { uploadIds } }) => {
      uploadIds.forEach((uploadId) => {
        const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

        if (uploadIndex >= 0) {
          state.uploadsArray[uploadIndex].status = UploadStatus.paused;
          state.uploadsArray[uploadIndex].xhr = undefined;
          if (state.uploadProgress[uploadId]) {
            state.uploadProgress[uploadId].progressBeforePause = state.uploadProgress[uploadId].progress;
          }
        }
      });
    })
    .addCase(setUploadsResumedAction, (state, { payload: { uploadIds } }) => {
      uploadIds.forEach((uploadId) => {
        const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

        if (uploadIndex >= 0) {
          state.uploadsArray[uploadIndex].status = UploadStatus.queued;
        }
      });
    })
    .addCase(setUploadedPartInfo, (state, { payload: { uploadId, totalBytesUploaded, uploadedPart } }) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].s3Info.totalBytesUploaded = totalBytesUploaded;
        if (state.uploadsArray[uploadIndex].s3Info.uploadedParts) {
          state.uploadsArray[uploadIndex].s3Info.uploadedParts?.push(uploadedPart);
        } else {
          state.uploadsArray[uploadIndex].s3Info.uploadedParts = [uploadedPart];
        }
      }
    })
    .addCase(setLargeUploadUrlInfoAction, (state, { payload: { uploadUrlInfo, uploadId } }) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].s3Info.uploadUrlInfo = uploadUrlInfo;
        if (uploadUrlInfo?.id) {
          state.uploadsArray[uploadIndex].clipId = uploadUrlInfo.id;
        }
      }
    })
    .addCase(setUploadUrlInfoAction, (state, { payload: { uploadId, uploadUrlInfo } }) => {
      const uploadIndex = state.uploadsArray.findIndex((u) => u.id === uploadId);

      if (uploadIndex >= 0) {
        state.uploadsArray[uploadIndex].s3Info.uploadUrlInfo = uploadUrlInfo;
      }
    });
});
